import { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { Link, useSearchParams } from 'react-router';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/solid';

import { Alert, Card, PageContainer, Spinner } from '@shared';

import { PasswordField } from '@/features/auth/PasswordField';
import { useTranslations } from '@/i18n/client';
import { createPortalApi } from '@/services/api';
import type { AuthApi } from '@/services/api/auth-api';
import { errorKeyOf, type ApiErrorKey } from '@/services/api/errors';
import {
  MIN_PASSWORD_LENGTH,
  PASSWORD_PATTERN,
  PASSWORD_REQUIREMENTS,
  SPECIAL_CHARS_HINT,
} from '@/features/auth/password-policy';

interface ResetPasswordFormValues {
  password: string;
  confirmPassword: string;
}

export interface ResetPasswordPageProps {
  /** Test-only injection point for the network boundary. */
  auth?: AuthApi;
}

function decodeToken(rawToken: string): string {
  try {
    return decodeURIComponent(rawToken);
  } catch {
    return rawToken;
  }
}

function normalizeResetToken(rawToken: string): string {
  const decoded = decodeToken(rawToken).trim();
  if (decoded.length === 30) {
    return decoded;
  }

  // Portal PasswordSet accepts only the 30-char token composed of 15-char
  // prefix + 15-char suffix. Extract it from longer tokens if needed.
  if (decoded.length > 30) {
    return `${decoded.slice(0, 15)}${decoded.slice(-15)}`;
  }

  return decoded;
}

export function ResetPasswordPage({ auth }: ResetPasswordPageProps) {
  const t = useTranslations('auth');
  const tErrors = useTranslations('errors.api');
  const [api] = useState(() => auth ?? createPortalApi().auth);
  const [params] = useSearchParams();
  const [submitting, setSubmitting] = useState(false);
  const [failure, setFailure] = useState<ApiErrorKey | null>(null);
  const [succeeded, setSucceeded] = useState(false);
  const [passwordHintsVisible, setPasswordHintsVisible] = useState(false);

  const email = params.get('email')?.trim() ?? '';
  const token = normalizeResetToken(params.get('token')?.trim() ?? '');
  const hasValidLink = email !== '' && token !== '';

  const {
    register,
    handleSubmit,
    getValues,
    control,
    formState: { errors },
  } = useForm<ResetPasswordFormValues>({
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });

  const password = useWatch({ control, name: 'password' }) ?? '';
  const passwordField = register('password', {
    required: t('resetPassword.errors.passwordRequired'),
    minLength: {
      value: MIN_PASSWORD_LENGTH,
      message: t('resetPassword.errors.passwordTooShort'),
    },
    pattern: {
      value: PASSWORD_PATTERN,
      message: t('resetPassword.errors.passwordRequirementsNotMet'),
    },
  });

  async function onSubmit(values: ResetPasswordFormValues): Promise<void> {
    if (!hasValidLink) {
      return;
    }

    setSubmitting(true);
    setFailure(null);

    try {
      await api.resetPassword(email, token, values.password);
      setSucceeded(true);
    } catch (error) {
      setFailure(errorKeyOf(error));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <PageContainer className="md-auth">
      <Card title={t('resetPassword.title')}>
        <p className="md-auth__subtitle">{t('resetPassword.subtitle')}</p>

        {!hasValidLink ? (
          <>
            <Alert variant="error">{tErrors('invalidResetLink')}</Alert>
            <p className="md-auth__link">
              <Link to="/forgot-password">{t('resetPassword.requestAnotherLink')}</Link>
            </p>
          </>
        ) : succeeded ? (
          <>
            <Alert variant="success">
              <strong>{t('resetPassword.successTitle')}</strong>
              <p>{t('resetPassword.successMessage')}</p>
            </Alert>
            <p className="md-auth__link">
              <Link to="/login">{t('resetPassword.loginLink')}</Link>
            </p>
          </>
        ) : (
          <form className="md-form" onSubmit={handleSubmit(onSubmit)} noValidate>
            {failure ? <Alert variant="error">{tErrors(failure)}</Alert> : null}

            <PasswordField
              label={t('resetPassword.passwordLabel')}
              autoComplete="new-password"
              invalid={Boolean(errors.password)}
              showLabel={t('resetPassword.showPasswordLabel')}
              hideLabel={t('resetPassword.hidePasswordLabel')}
              {...(errors.password?.message ? { message: errors.password.message } : {})}
              {...passwordField}
              onFocus={() => setPasswordHintsVisible(true)}
              onBlur={(event) => {
                void passwordField.onBlur(event);
                setPasswordHintsVisible(false);
              }}
            />

            {passwordHintsVisible ? (
              <ul className="md-password-hints" aria-label={t('resetPassword.passwordRequirementsLabel')}>
                {PASSWORD_REQUIREMENTS.map((requirement) => {
                  const fulfilled = requirement.test(password);
                  return (
                    <li key={requirement.key} className="md-password-hints__item" data-fulfilled={fulfilled}>
                      {fulfilled ? (
                        <CheckCircleIcon
                          className="md-password-hints__icon md-password-hints__icon--ok"
                          aria-hidden="true"
                        />
                      ) : (
                        <XCircleIcon
                          className="md-password-hints__icon md-password-hints__icon--fail"
                          aria-hidden="true"
                        />
                      )}
                      <span>
                        {t(`resetPassword.passwordRequirements.${requirement.key}`)}
                        {requirement.key === 'special' ? ` ${SPECIAL_CHARS_HINT}` : ''}
                      </span>
                    </li>
                  );
                })}
              </ul>
            ) : null}

            <PasswordField
              label={t('resetPassword.confirmPasswordLabel')}
              autoComplete="new-password"
              invalid={Boolean(errors.confirmPassword)}
              showLabel={t('resetPassword.showPasswordLabel')}
              hideLabel={t('resetPassword.hidePasswordLabel')}
              {...(errors.confirmPassword?.message ? { message: errors.confirmPassword.message } : {})}
              {...register('confirmPassword', {
                required: t('resetPassword.errors.confirmPasswordRequired'),
                validate: (value) =>
                  value === getValues('password') || t('resetPassword.errors.passwordsDoNotMatch'),
              })}
            />

            <button
              type="submit"
              className="zs-button"
              disabled={submitting}
              aria-busy={submitting || undefined}
            >
              {submitting ? (
                <Spinner label={t('resetPassword.submitButton')} />
              ) : (
                t('resetPassword.submitButton')
              )}
            </button>

            <p className="md-auth__link">
              <Link to="/login">{t('resetPassword.loginLink')}</Link>
            </p>
          </form>
        )}
      </Card>
    </PageContainer>
  );
}
